/**
 * External drag receiver — accepts drags that START OUTSIDE the calendar:
 * shelf card/slot grips, pinboard pins, cancellation-drawer cards and
 * dynamic-template chips. All of these are `.fc-event[data-event]` elements
 * (their payload is the existing `DraggableEventData` / template JSON), and
 * they stay registered with FullCalendar's `Draggable` for the old calendar —
 * we deliberately coexist with it: its floating mirror keeps providing the
 * "what am I dragging" feedback everywhere, while THIS hook does the actual
 * grid hit-testing, the snapped in-grid ghost, validity tinting and the drop
 * dispatch for the MobiliCalendar surface.
 *
 * Design rules (same refresh-safety doctrine as internal moves):
 *  - The drag is driven ONLY by document-level pointer events + the parsed
 *    payload — never by DOM nodes of the grid, so snapshot re-renders during
 *    the hover can never abort it.
 *  - Per-frame state (cell, validity) lives in the interaction mini store;
 *    only the ghost overlay re-renders while hovering.
 *  - On drop the ghost stays visible in a "settling" state until the
 *    receive's optimistic event shows up in the index (or the bridge promise
 *    settles), bridging the same commit→onMutate gap pending moves cover.
 */
import { useCallback, useEffect, useRef } from "react";
import {
  MCCellPos,
  MCExternalDrop,
  MCExternalPayload,
  MCGridConfig,
  MCInteractionState,
  MCResource,
} from "./model/types";
import { MCEventIndex } from "./model/eventIndex";
import { parseDurationToMin, validateExternalRange } from "./model/selection";
import { clampSlot } from "./model/gridGeometry";
import { MiniStore } from "./model/miniStore";

const DRAG_THRESHOLD_PX = 5;
/** Give up on a settling ghost if neither match nor settle arrives. */
const SETTLE_TIMEOUT_MS = 8_000;

interface ExternalDragOptions {
  cfg: MCGridConfig;
  store: MiniStore<MCInteractionState>;
  indexRef: React.RefObject<MCEventIndex>;
  resourcesRef: React.RefObject<MCResource[]>;
  rootRef: React.RefObject<HTMLDivElement>;
  bodyRef: React.RefObject<HTMLDivElement>;
  /** Displayed day — target Dates are built from it at drop time. */
  dateRef: React.RefObject<Date>;
  cellFromClient: (clientX: number, clientY: number) => MCCellPos | null;
  edgeAutoScroll: (clientX: number, clientY: number) => void;
  onDrop?: (drop: MCExternalDrop) => void | Promise<unknown>;
}

interface ArmedDrag {
  el: HTMLElement;
  startX: number;
  startY: number;
  active: boolean;
  payload: MCExternalPayload | null;
  durationMin: number;
}

let externalToken = 0;

export const useExternalDrag = ({
  cfg,
  store,
  indexRef,
  resourcesRef,
  rootRef,
  bodyRef,
  dateRef,
  cellFromClient,
  edgeAutoScroll,
  onDrop,
}: ExternalDragOptions) => {
  const armedRef = useRef<ArmedDrag | null>(null);
  const rafRef = useRef(0);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  const clearGhost = useCallback(() => {
    if (store.get().external) store.set({ external: null });
  }, [store]);

  /** Retire a settling ghost, but only if it is still the same drop. */
  const clearSettling = useCallback(
    (token: number) => {
      const external = store.get().external;
      if (external?.settling && external.token === token) {
        store.set({ external: null });
      }
    },
    [store],
  );

  const pointerInsideBody = useCallback(
    (clientX: number, clientY: number): boolean => {
      const rect = bodyRef.current?.getBoundingClientRect();
      return (
        !!rect &&
        clientX >= rect.left &&
        clientX < rect.right &&
        clientY >= rect.top &&
        clientY < rect.bottom
      );
    },
    [bodyRef],
  );

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.(".fc-event[data-event]") as HTMLElement | null;
      if (!el) return;
      // Only sources OUTSIDE the calendar are external.
      if (rootRef.current?.contains(el)) return;
      armedRef.current = {
        el,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
        payload: null,
        durationMin: 30,
      };
    };

    const updateGhost = (clientX: number, clientY: number) => {
      const drag = armedRef.current;
      const index = indexRef.current;
      const resources = resourcesRef.current ?? [];
      if (!drag?.active || !drag.payload || !index) return;

      if (!pointerInsideBody(clientX, clientY)) {
        clearGhost();
        return;
      }
      const cell = cellFromClient(clientX, clientY);
      if (!cell) {
        clearGhost();
        return;
      }
      const startMin = clampSlot(cfg, cell.slotMin);
      const endMin = startMin + drag.durationMin;
      const previous = store.get().external;
      if (
        previous &&
        !previous.settling &&
        previous.resourceIndex === cell.resourceIndex &&
        previous.startMin === startMin
      ) {
        return; // same cell — nothing to update
      }
      store.set({
        external: {
          payload: drag.payload,
          resourceIndex: cell.resourceIndex,
          startMin,
          endMin,
          valid: validateExternalRange(index, resources, cell.resourceIndex, startMin, endMin, cfg),
          settling: false,
          token: ++externalToken,
        },
      });
      edgeAutoScroll(clientX, clientY);
    };

    const onPointerMove = (e: PointerEvent) => {
      const drag = armedRef.current;
      if (!drag) return;

      if (!drag.active) {
        const moved = Math.abs(e.clientX - drag.startX) + Math.abs(e.clientY - drag.startY);
        if (moved < DRAG_THRESHOLD_PX) return;
        // Activation: parse the payload once; a garbled attribute disarms.
        try {
          drag.payload = JSON.parse(drag.el.getAttribute("data-event") ?? "") as MCExternalPayload;
        } catch {
          armedRef.current = null;
          return;
        }
        if (!drag.payload?.id) {
          armedRef.current = null;
          return;
        }
        drag.durationMin = parseDurationToMin(drag.payload.duration);
        drag.active = true;
      }

      const { clientX, clientY } = e;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        updateGhost(clientX, clientY);
      });
    };

    const dispatchDrop = (drag: ArmedDrag, clientX: number, clientY: number) => {
      const index = indexRef.current;
      const resources = resourcesRef.current ?? [];
      const date = dateRef.current;
      if (!drag.payload || !index || !date) return clearGhost();
      if (!pointerInsideBody(clientX, clientY)) return clearGhost();
      const cell = cellFromClient(clientX, clientY);
      if (!cell) return clearGhost();

      const startMin = clampSlot(cfg, cell.slotMin);
      const endMin = startMin + drag.durationMin;
      const resource = resources[cell.resourceIndex];
      if (
        !resource ||
        !validateExternalRange(index, resources, cell.resourceIndex, startMin, endMin, cfg)
      ) {
        return clearGhost();
      }

      const start = new Date(date);
      start.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
      const end = new Date(start.getTime() + drag.durationMin * 60_000);

      // Keep the ghost as a settling placeholder until the optimistic event
      // arrives (index-match effect in MobiliCalendar) or the receive settles.
      const token = ++externalToken;
      store.set({
        external: {
          payload: drag.payload,
          resourceIndex: cell.resourceIndex,
          startMin,
          endMin,
          valid: true,
          settling: true,
          token,
        },
      });
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(() => clearSettling(token), SETTLE_TIMEOUT_MS);

      const result = onDropRef.current?.({
        payload: drag.payload,
        resourceId: resource.id,
        start,
        end,
      });
      Promise.resolve(result)
        .catch(() => {})
        .then(() => clearSettling(token));
    };

    const onPointerUp = (e: PointerEvent) => {
      const drag = armedRef.current;
      if (!drag) return;
      armedRef.current = null;
      if (!drag.active) return;
      dispatchDrop(drag, e.clientX, e.clientY);
    };

    const onPointerCancel = () => {
      if (!armedRef.current) return;
      armedRef.current = null;
      clearGhost();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && armedRef.current?.active) {
        armedRef.current = null;
        clearGhost();
      }
    };

    document.addEventListener("pointerdown", onPointerDown, { capture: true });
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, { capture: true } as any);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerCancel);
      document.removeEventListener("keydown", onKeyDown);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, [
    cfg,
    store,
    indexRef,
    resourcesRef,
    rootRef,
    bodyRef,
    dateRef,
    cellFromClient,
    edgeAutoScroll,
    pointerInsideBody,
    clearGhost,
    clearSettling,
  ]);
};
